"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { FiTrendingUp, FiDollarSign, FiUsers, FiHeart, FiTarget, FiClock, FiChevronDown, FiEye, FiEyeOff } from "react-icons/fi";
import { LayoutGridIcon } from "@/app/components/icons/LayoutGridIcon";
import { LayoutListIcon } from "@/app/components/icons/LayoutListIcon";
import { useAuth } from "@/hooks/useAuth";
import { useT } from "@/hooks/useT";
import { FilterButton } from "./AdvancedFiltersDrawer";
import HorizontalScroll from "@/app/components/HorizontalScroll";

const SEARCH_HISTORY_KEY = "frenflow_trader_search_history";
const MAX_HISTORY_ITEMS = 5;

interface SearchHistoryItem {
  address: string;
  username: string | null;
  profile_image: string | null;
  timestamp: number;
}

interface TraderSuggestion {
  address: string;
  username: string | null;
  profile_image: string | null;
  pnl: number;
  rank: number;
  isFrenFlowUser?: boolean;
  frenFlowUsername?: string | null;
  frenFlowAvatar?: string | null;
}

interface UsersFiltersProps {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  selectedFilter: string;
  setSelectedFilter: (value: string) => void;
  selectedMarket: string;
  setSelectedMarket: (value: string) => void;
  sortBy: string;
  setSortBy: (value: string) => void;
  resultsCount: number;
  viewMode: "grid" | "list";
  setViewMode: (value: "grid" | "list") => void;
  source?: string;
  timePeriod?: string;
  setTimePeriod?: (value: string) => void;
  advancedFilterCount?: number;
  onOpenFilters?: () => void;
  /** Founder/partner X-only toggle props. When showXOnlyToggle=false
   *  (default for everyone except the allow-listed accounts) the
   *  toggle button is not rendered at all. */
  showXOnlyToggle?: boolean;
  xOnlyEnabled?: boolean;
  onToggleXOnly?: () => void;
  /** Live count of traders the logged-in user has marked as favorite.
   *  When > 0 the "Favorites" pill is rendered at the start of the
   *  filter row; when 0 it stays hidden so the bar isn't cluttered
   *  with an empty filter. Reacts immediately when the user toggles
   *  a star — no page refresh needed since the parent passes the
   *  derived count straight from the watchlist state. */
  watchlistCount?: number;
  /** When Predexon is down the style pills collapse to just "All"; the parent
   *  passes the time-window pills here to fill the (now empty) desktop pill
   *  slot next to Sort. Rendered ONLY on desktop and ONLY when HIDE_PREDEXON —
   *  mobile keeps its own time-window row below (unchanged). */
  timeWindowSlot?: React.ReactNode;
}

export default function UsersFilters({
  searchTerm,
  setSearchTerm,
  selectedFilter,
  setSelectedFilter,
  selectedMarket,
  setSelectedMarket,
  sortBy,
  setSortBy,
  resultsCount,
  viewMode,
  setViewMode,
  showXOnlyToggle = false,
  xOnlyEnabled = false,
  onToggleXOnly,
  source = "polymarket",
  timePeriod = "all_time",
  setTimePeriod,
  advancedFilterCount = 0,
  onOpenFilters,
  watchlistCount = 0,
  timeWindowSlot,
}: UsersFiltersProps) {
  const t = useT();
  const { isLoggedIn } = useAuth();
  const [isFocused, setIsFocused] = useState(false);
  const [suggestions, setSuggestions] = useState<TraderSuggestion[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [searchHistory, setSearchHistory] = useState<SearchHistoryItem[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load search history from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(SEARCH_HISTORY_KEY);
      if (saved) {
        setSearchHistory(JSON.parse(saved));
      }
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  // Save to search history
  const addToHistory = useCallback((trader: TraderSuggestion | SearchHistoryItem) => {
    setSearchHistory((prev) => {
      const filtered = prev.filter((item) => item.address !== trader.address);
      const newItem: SearchHistoryItem = {
        address: trader.address,
        username: trader.username,
        profile_image: trader.profile_image,
        timestamp: Date.now(),
      };
      const updated = [newItem, ...filtered].slice(0, MAX_HISTORY_ITEMS);
      try {
        localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(updated));
      } catch {
        // Ignore localStorage errors
      }
      return updated;
    });
  }, []);

  // Keyboard shortcut Cmd/Ctrl+K to focus search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
      // Escape to blur
      if (e.key === "Escape" && document.activeElement === inputRef.current) {
        inputRef.current?.blur();
        setIsFocused(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Fetch suggestions when search term changes
  useEffect(() => {
    if (!searchTerm || searchTerm.length < 2) {
      setSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoadingSuggestions(true);
      try {
        const sourceParam = isKalshi ? '&source=kalshi' : '';
        const res = await fetch(`/api/traders/search?q=${encodeURIComponent(searchTerm.replace(/^@/, ''))}&limit=5${sourceParam}`);
        if (res.ok) {
          const data = await res.json();
          setSuggestions(data.traders || []);
        }
      } catch (err) {
        // Silently fail
      } finally {
        setLoadingSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setIsFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isKalshi = source === "kalshi";

  // ── Predexon-backed filters (real API params) ──
  // 2026-05-20: "Favorites" pill (id stays as 'watchlist' for backend
  // compatibility, label changes to "Favorites" / "Favoritos"):
  //   • Hidden entirely when the user has 0 favs — empty filter clutters
  //     the bar without showing anything actionable.
  //   • Renders FIRST (before "All") when count > 0 — it's the most
  //     personal filter so it gets the prime slot.
  //   • Styled in lime to stand out from the neutral pills.
  // 2026-07-12: mientras Predexon está caído (403), sus controles dan resultados
  // vacíos (los style/metric filters enrutan a Predexon; los sorts roi/win_rate/
  // profit_factor son Predexon-only). Se ocultan con el MISMO flag que las tabs
  // del event page — un solo switch; al pagar Predexon, flip y vuelve todo.
  const HIDE_PREDEXON = process.env.NEXT_PUBLIC_HIDE_PREDEXON_TABS === "true";
  const PREDEXON_FILTER_IDS = new Set([
    "whales", "degen", "contrarian", "value", "conviction", "market-maker",
    "profitable", "high-roi", "sharp",
  ]);
  const PREDEXON_SORT_IDS = new Set(["roi", "win_rate", "profit_factor"]);

  const filtersAll = isKalshi
    ? [{ id: "all", label: t("traders.filter.all"), icon: FiTarget }]
    : [
        ...(isLoggedIn && watchlistCount > 0
          ? [{ id: "watchlist", label: t("traders.filter.favorites"), icon: FiHeart }]
          : []),
        { id: "all", label: t("traders.filter.all"), icon: FiTarget },
        // Style-based (Predexon style= param)
        { id: "whales", label: t("traders.filter.whales"), icon: FiDollarSign },
        { id: "degen", label: t("traders.filter.degens"), icon: FiClock },
        { id: "contrarian", label: t("traders.filter.contrarian"), icon: FiTarget },
        { id: "value", label: t("traders.filter.value"), icon: FiTrendingUp },
        { id: "conviction", label: t("traders.filter.conviction"), icon: FiDollarSign },
        { id: "market-maker", label: t("traders.filter.marketMakers"), icon: FiUsers },
        // Metric presets (Predexon min_ params)
        { id: "profitable", label: t("traders.filter.profitable"), icon: FiTrendingUp },
        { id: "high-roi", label: t("traders.filter.highRoi"), icon: FiTrendingUp },
        { id: "sharp", label: t("traders.filter.sharp"), icon: FiTarget },
        // HFT moved OUT of the style quick-filters into a dedicated toggle next
        // to the time-window pills (TradersTimeWindowPills). It's a trader-type
        // switch ("show bots"), not a style, so it reads clearer there and the
        // general list hides HFT by default (backend excludeHft).
      ];
  const filters = filtersAll.filter(
    (f) => !(HIDE_PREDEXON && PREDEXON_FILTER_IDS.has(f.id)),
  );

  // windowOptions + kalshiTimeOptions used to live here for the in-header
  // time-window pills; the pills moved to <TradersTimeWindowPills> (below
  // in this same file) which has its own local copies — the originals
  // here were dead after the 2026-05-24 layout refactor.

  const sortOptionsAll = isKalshi
    ? [
        { id: "pnl", label: t("traders.sort.profit") },
        { id: "volume", label: t("traders.sort.volume") },
        { id: "trades", label: t("traders.sort.predictions") },
      ]
    : [
        { id: "rank", label: t("traders.sort.rank") },
        { id: "pnl", label: t("traders.sort.pnl") },
        { id: "volume", label: t("traders.sort.volume") },
        { id: "roi", label: t("traders.sort.roi") },
        { id: "win_rate", label: t("traders.sort.winRate") },
        { id: "profit_factor", label: t("traders.sort.profitFactor") },
      ];
  const sortOptions = sortOptionsAll.filter(
    (o) => !(HIDE_PREDEXON && PREDEXON_SORT_IDS.has(o.id)),
  );

  // ── Reusable pill renderers (shared across mobile + desktop rows) ──
  // renderTimePill removed 2026-05-24 — the in-header time row no longer
  // exists; <TradersTimeWindowPills> renders its own buttons inline.
  // 2026-05-25: sort moved from a 6-pill scroll-x row to a dropdown
  // for BOTH desktop and mobile. The pill row was eating real estate
  // (especially mobile where it pushed the style/filter pills out of
  // view) and the lesser-used sorts (ROI / Win Rate / Profit Factor)
  // didn't justify the always-visible weight. <SortDropdown> below
  // replaces both `renderSortPill` invocations.
  const renderFilterPill = (filter: { id: string; label: string; icon: React.ComponentType<{ className?: string }> }, sizeClass: string) => {
    const Icon = filter.icon;
    const isSelected = selectedFilter === filter.id;
    // Favorites pill (id=watchlist) stands out from the neutral pills with a
    // white/contrast border (lime retired 2026-06-10). Selected = white-solid
    // like the other active pills; idle = neutral fill + the distinguishing
    // border so it still reads as "the user's curated list".
    const isFav = filter.id === 'watchlist';
    const className = isFav
      ? `${sizeClass} flex items-center gap-1 rounded-[5px] font-semibold whitespace-nowrap transition-colors flex-shrink-0 ${
          isSelected
            ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 border border-gray-300 dark:border-white/60'
            : 'bg-black/[0.04] dark:bg-white/[0.06] text-gray-700 dark:text-gray-200 hover:bg-black/[0.12] dark:hover:bg-white/[0.14] border border-gray-300 dark:border-white/60'
        }`
      : `${sizeClass} flex items-center gap-1 rounded-[5px] font-semibold whitespace-nowrap transition-colors flex-shrink-0 ${
          isSelected
            ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
            : 'bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]'
        }`;
    return (
      <button
        key={filter.id}
        onClick={() => setSelectedFilter(filter.id)}
        className={className}
      >
        <Icon className="w-3 h-3" />
        {filter.label}
      </button>
    );
  };
  // Sort dropdown — opens on click, closes on outside-click or option
  // select. Used in both desktop + mobile rows (no more sort-pill
  // overflow scroll). Self-contained refs/handlers so the parent
  // doesn't need a separate state for open/close.
  const SortDropdown = ({ sizeClass }: { sizeClass: string }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
      if (!open) return;
      const onDocClick = (e: MouseEvent) => {
        if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
      };
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setOpen(false);
      };
      document.addEventListener('mousedown', onDocClick);
      document.addEventListener('keydown', onKey);
      return () => {
        document.removeEventListener('mousedown', onDocClick);
        document.removeEventListener('keydown', onKey);
      };
    }, [open]);
    const current = sortOptions.find((o) => o.id === sortBy) ?? sortOptions[0];
    return (
      <div ref={ref} className="relative flex-shrink-0">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={`${sizeClass} inline-flex items-center gap-1 rounded-[5px] font-semibold whitespace-nowrap transition-colors bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100`}
        >
          <span>{t("traders.sort.label")}: {current?.label}</span>
          <FiChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div
            role="listbox"
            className="absolute left-0 top-[calc(100%+4px)] z-20 min-w-[160px] py-1 rounded-[5px] bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] shadow-lg"
          >
            {sortOptions.map((opt) => {
              const isActive = opt.id === sortBy;
              return (
                <button
                  key={opt.id}
                  role="option"
                  aria-selected={isActive}
                  onClick={() => {
                    setSortBy(opt.id);
                    setOpen(false);
                  }}
                  className={`w-full text-left px-3 py-1.5 text-xs font-semibold transition-colors ${
                    isActive
                      ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                      : 'text-gray-700 dark:text-gray-200 hover:bg-black/[0.05] dark:hover:bg-white/[0.06]'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };
  const renderViewToggle = () => (
    <div className="flex gap-0.5 bg-black/[0.03] dark:bg-white/[0.04] rounded-[5px] p-0.5 flex-shrink-0">
      <button
        onClick={() => setViewMode("grid")}
        className={`p-1.5 rounded-[5px] transition-colors ${
          viewMode === "grid"
            ? "bg-gray-900 dark:bg-white text-white dark:text-black"
            : "text-gray-600 dark:text-gray-400 hover:bg-black/[0.06] dark:hover:bg-white/[0.08]"
        }`}
        aria-label={t("traders.view.grid")}
      >
        <LayoutGridIcon size={14} />
      </button>
      <button
        onClick={() => setViewMode("list")}
        className={`p-1.5 rounded-[5px] transition-colors ${
          viewMode === "list"
            ? "bg-gray-900 dark:bg-white text-white dark:text-black"
            : "text-gray-600 dark:text-gray-400 hover:bg-black/[0.06] dark:hover:bg-white/[0.08]"
        }`}
        aria-label={t("traders.view.list")}
      >
        <LayoutListIcon size={14} />
      </button>
    </div>
  );
  const renderXOnlyToggle = () => (
    showXOnlyToggle && (
      <button
        onClick={onToggleXOnly}
        aria-pressed={xOnlyEnabled}
        title={xOnlyEnabled ? t("traders.filter.xOnly.tooltip.on") : t("traders.filter.xOnly.tooltip.off")}
        className={`inline-flex items-center gap-1 px-2 py-1.5 rounded-[5px] text-[11px] font-inter-tight font-bold transition-colors border flex-shrink-0 ${
          xOnlyEnabled
            ? 'bg-gray-900 dark:bg-white border-transparent text-white dark:text-gray-900'
            : 'bg-white dark:bg-[#18181D] border-gray-200 dark:border-[#1A1A1A] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.04]'
        }`}
      >
        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
        <span className="hidden sm:inline">{t("traders.filter.xOnly")}</span>
      </button>
    )
  );
  // renderCounter removed 2026-05-24 — the "X traders" pill no longer
  // renders inside the header. It now sits next to the "Showing X-Y"
  // pager beneath the grid (UsersGridWrapper.tsx). The `resultsCount`
  // prop is still passed so external consumers don't break; the value
  // flows through to the pager via `totalTraders` upstream.
  // (timeOptions used to be computed here for the in-header window pills;
  // the pills moved to <TradersTimeWindowPills> which has its own local
  // copy — see line ~477 — so this header variable was dead code.)

  return (
    <div>
      {/* Single-row layout (2026-05-24): sort + style chips on the left
          (scroll-x), then Filter button + X-only toggle + grid/list view
          toggle pinned to the right. Counter moved out of the header — it
          now sits next to "Showing X-Y" beneath the grid (see
          UsersGridWrapper.tsx) because the total count was duplicating the
          pager and inflating the visual weight of the header.

          MOBILE: chips scroll-x with the right cluster wrapping below. We
          do this by stacking them as a flex-wrap; touch users tap the
          chips inline. DESKTOP: justify-between with flex-1 on the chip
          scroller so the right cluster stays pinned. */}
      {/* Single-row layout:
          [Sort ▾] · gap · [All / Whales / Degens ...] · [Filters · X · view]
          Sort sits OUTSIDE the scroll/Hscroll container so the absolute-
          positioned menu isn't clipped (regression caught 2026-05-25).
          ml-3 on the scrollable cluster gives the visual breathing room
          between the dropdown and the first pill ("All"). */}
      {/* MOBILE — two rows (2026-06). A single row crammed the Sort dropdown,
          the ~10 category pills and the right cluster into ~390px, leaving the
          category scroller a sliver (only ~2 pills visible before "Degens" got
          clipped). Now:
            Row 1 → Sort (left) · Filters + X + grid/list (right)
            Row 2 → full-width category scroller (Favorites / All / Whales / …). */}
      <div className="flex flex-col gap-2.5 lg:hidden">
        {/* Row 1: sort (left) + filters/X/view (right) */}
        <div className="flex items-center justify-between gap-1.5">
          <SortDropdown sizeClass="px-2.5 py-[6px] text-[11px]" />
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {!isKalshi && !HIDE_PREDEXON && onOpenFilters && (
              <FilterButton activeCount={advancedFilterCount} onClick={onOpenFilters} />
            )}
            {renderXOnlyToggle()}
            {renderViewToggle()}
          </div>
        </div>
        {/* Row 2: category pills, full width scroll-x. Hidden when the only
            remaining pill is "All" (Predexon style pills removed during the
            outage) so mobile doesn't show a lone, useless "All". */}
        {!isKalshi && filters.length > 1 && (
          <HorizontalScroll wrapperClassName="min-w-0" className="flex items-center gap-1.5" fadeFrom="bg-white dark:bg-[#0f0f12]">
            {filters.map(f => renderFilterPill(f, "px-2.5 py-[6px] text-[11px]"))}
          </HorizontalScroll>
        )}
      </div>
      {/* DESKTOP */}
      <div className="hidden lg:flex items-center gap-1.5">
        <SortDropdown sizeClass="px-3 py-1.5 text-xs" />
        <HorizontalScroll
          wrapperClassName="flex-1 min-w-0 ml-3"
          className="flex items-center gap-1.5"
          fadeFrom="bg-white dark:bg-[#0f0f12]"
        >
          {/* Desktop: with Predexon down the style pills collapse to just
              "All", so use this slot for the time-window pills instead (next to
              Sort). Mobile is untouched — it keeps its own time-window row. */}
          {!isKalshi && (HIDE_PREDEXON && timeWindowSlot
            ? timeWindowSlot
            : filters.map(f => renderFilterPill(f, "px-3 py-1.5 text-xs")))}
        </HorizontalScroll>
        {/* Right cluster — pinned, NOT scrolled. */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {!isKalshi && !HIDE_PREDEXON && onOpenFilters && (
            <FilterButton activeCount={advancedFilterCount} onClick={onOpenFilters} />
          )}
          {renderXOnlyToggle()}
          {renderViewToggle()}
        </div>
      </div>
    </div>
  );
}

/**
 * Standalone time-window pills, designed to sit OUTSIDE the fixed header,
 * directly above the grid/list. Exported so UsersGridWrapper can place it
 * flush with the cards (the in-header version separated by ~80px of fixed
 * spacer was hard to discover; this answers user feedback asking for the
 * selector "right above the cards").
 */
export function TradersTimeWindowPills({
  timePeriod,
  setTimePeriod,
  source = "polymarket",
  hftActive = false,
  onToggleHft,
  inline = false,
}: {
  timePeriod: string;
  setTimePeriod: (value: string) => void;
  source?: string;
  /** Whether the "show HFT bots" toggle is on (selectedFilter === 'hft'). */
  hftActive?: boolean;
  /** Toggles the HFT filter. Undefined hides the toggle (e.g. non-polymarket). */
  onToggleHft?: () => void;
  /** Inline mode: rendered inside the desktop filter bar next to Sort (no
   *  bottom margin, no justify-between) so it aligns vertically with Sort. */
  inline?: boolean;
}) {
  const t = useT();
  const isKalshi = source === "kalshi";
  const windowOptions = [
    { id: "1d", label: t("traders.window.1d") },
    { id: "7d", label: t("traders.window.7d") },
    { id: "30d", label: t("traders.window.30d") },
    { id: "all_time", label: t("traders.window.allTime") },
  ];
  const kalshiTimeOptions = [
    { id: "all_time", label: t("traders.window.kalshi.all") },
    { id: "daily", label: t("traders.window.kalshi.daily") },
    { id: "weekly", label: t("traders.window.kalshi.weekly") },
    { id: "monthly", label: t("traders.window.kalshi.monthly") },
    { id: "yearly", label: t("traders.window.kalshi.yearly") },
  ];
  const timeOptions = isKalshi ? kalshiTimeOptions : windowOptions;

  return (
    // Aligned toolbar row: time-window pills on the LEFT, HFT toggle on the
    // RIGHT (justify-between). No centered pills / absolute HFT — that left a
    // dead gap in the middle and didn't line up with the filter bar above.
    <div className={`flex items-center gap-2 ${inline ? "justify-center flex-1" : "justify-between mb-4"}`}>
      {/* Time-window pills (one responsive row) */}
      <div className="flex items-center gap-1.5 min-w-0 overflow-x-auto scrollbar-none">
        {timeOptions.map(opt => (
          <button
            key={opt.id}
            onClick={() => setTimePeriod(opt.id)}
            className={`px-2.5 lg:px-3 py-[6px] lg:py-1.5 text-[11px] lg:text-xs rounded-[5px] font-semibold whitespace-nowrap transition-colors flex-shrink-0 ${
              timePeriod === opt.id
                ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900"
                : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* HFT toggle — right-aligned (a different axis: trader type, not time).
          Off by default: the general list hides bots; on surfaces only HFT. */}
      {onToggleHft && (
        <button
          type="button"
          onClick={onToggleHft}
          title={t("traders.curated.hftHint")}
          className={`inline-flex items-center gap-1.5 px-2.5 lg:px-3 py-[6px] lg:py-1.5 rounded-[5px] text-[11px] lg:text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors flex-shrink-0 ${
            hftActive
              ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900"
              : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
          }`}
        >
          {hftActive ? <FiEyeOff className="w-3.5 h-3.5" /> : <FiEye className="w-3.5 h-3.5" />}
          <span className="hidden sm:inline">{hftActive ? t("traders.filter.hideHftBots") : t("traders.filter.showHftBots")}</span>
          <span className="sm:hidden">HFT</span>
        </button>
      )}
    </div>
  );
}
